import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  MapPin,
  Home,
  Bath,
  Layers,
  Maximize2,
  Calendar,
  Phone,
  ArrowLeft,
  Eye,
  CheckCircle,
  AlertCircle,
  Building2,
  Trees,
  Video,
  ExternalLink,
  Copy,
  Navigation,
} from "lucide-react";
import FormatPrice from "../shared/PriceFormatter";
import FormatDateTime from "../../../../shared/utils/dateUtils";
import { DEFAULT_AVATAR } from "../../../../../utils/fallbackImages";

const getToken = () => Cookies.get("jwt");

const TYPE_LABELS = {
  house: "บ้าน",
  condo: "คอนโด",
  land: "ที่ดิน",
  townhouse: "ทาวน์เฮาส์",
  commercial: "พาณิชย์",
  other: "อื่น ๆ",
};

export default function ViewProduct() {
  const { id: idAsset } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sharer = searchParams.get("sharer");

  const [data, setData] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [nearbyOpen, setNearbyOpen] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (!idAsset) return;

    const url = sharer
      ? `${process.env.REACT_APP_API}/product/${idAsset}?sharer=${sharer}`
      : `${process.env.REACT_APP_API}/product/${idAsset}`;

    axios
      .get(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((res) => {
        setData(res.data.asset);
        setOwnerData(res.data.ownerData);
        setShareInfo(res.data.Share);
      })
      .catch(() => {
        Swal.fire("ไม่พบทรัพย์สิน", "ทรัพย์นี้อาจถูกลบหรือไม่มีอยู่", "error")
          .then(() => navigate(-1));
      })
      .finally(() => setIsLoading(false));
  }, [idAsset]);

  const images = data?.images_url?.AssetImagesURL ?? [];
  const prevSlide = () => setSlideIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const nextSlide = () => setSlideIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  const handleAddToCart = () => {
    axios
      .post(`${process.env.REACT_APP_API}/event/addBasket?assetId=${idAsset}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then(() => Swal.fire({ title: "เพิ่มในรายการถูกใจแล้ว", icon: "success", timer: 1500, showConfirmButton: false }))
      .catch(() => Swal.fire("ไม่สำเร็จ", "กรุณาเข้าสู่ระบบก่อน", "warning"));
  };

  const handleShare = async () => {
    const link = `${window.location.origin}/product/${idAsset}`;
    await navigator.clipboard.writeText(link);
    Swal.fire({ title: "คัดลอกลิงก์แล้ว", icon: "success", timer: 1500, showConfirmButton: false });
  };

  const handleGetContact = () => {
    if (contactData) { setShowContact(true); return; }
    setIsLoadingContact(true);
    axios
      .post(`${process.env.REACT_APP_API}/event/getContact`,
        { assetId: idAsset, OwnerUid: ownerData?.uid, sharer },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      )
      .then((res) => { setContactData(res.data); setShowContact(true); })
      .catch(() => Swal.fire("ไม่สำเร็จ", "กรุณาเข้าสู่ระบบก่อน", "warning"))
      .finally(() => setIsLoadingContact(false));
  };

  const nearbyCategories = data?.nearby_location
    ? Object.entries(data.nearby_location).filter(([, v]) => Array.isArray(v) && v.length > 0)
    : [];

  const categoryLabel = (key) => ({
    schools: "โรงเรียน",
    airports: "สนามบิน",
    bts: "BTS",
    mrt: "MRT",
    hospitals: "โรงพยาบาล",
    shoppingMalls: "ห้างสรรพสินค้า",
    trainStations: "สถานีรถไฟ",
    customName: "สถานที่อื่น ๆ",
  }[key] ?? key);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-sm">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const isSoldOrBanned = data.sold || data.ban;
  const typeLabel = TYPE_LABELS[data.type] ?? data.type ?? "";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-4 lg:pt-24">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3"
        >
          <button onClick={() => navigate(-1)}>
            <div className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 hover:bg-gray-100 transition-colors flex-shrink-0">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </div>
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">ทรัพย์สิน</p>
            <p className="text-base font-bold text-gray-900 truncate">{data.id}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              className="w-9 h-9 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={handleAddToCart}
              className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center border border-red-100 hover:bg-red-100 transition-colors"
            >
              <Heart className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </motion.div>

        {/* ── Image Carousel ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.04 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="relative w-full h-72 sm:h-96 bg-gray-100">
            {images.length > 0 ? (
              <>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={slideIndex}
                    src={images[slideIndex]}
                    alt={data.title}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  />
                </AnimatePresence>

                {isSoldOrBanned && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-20">
                    <div className="bg-white/10 border border-white/20 px-6 py-4 rounded-2xl text-center">
                      <AlertCircle className="w-8 h-8 text-white mx-auto mb-2" />
                      <p className="text-white text-sm font-semibold">ทรัพย์นี้ถูกลบหรือขายแล้ว</p>
                    </div>
                  </div>
                )}

                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/60 transition-colors z-10"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/60 transition-colors z-10"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSlideIndex(i)}
                      className={`rounded-full transition-all ${i === slideIndex ? "w-5 h-2 bg-white" : "w-2 h-2 bg-white/50"}`}
                    />
                  ))}
                </div>

                <div className="absolute top-3 left-3 flex gap-2 z-10">
                  {data.official && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-blue-500/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                      <CheckCircle className="w-3 h-3" /> Official
                    </span>
                  )}
                  {typeLabel && (
                    <span className="px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                      {typeLabel}
                    </span>
                  )}
                </div>

                <div className="absolute top-3 right-3 z-10">
                  <span className="px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                    {slideIndex + 1} / {images.length}
                  </span>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <Home className="w-16 h-16" />
              </div>
            )}
          </div>

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto scrollbar-hide">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSlideIndex(i)}
                  className={`flex-shrink-0 w-16 h-12 rounded-xl overflow-hidden border-2 transition-all ${i === slideIndex ? "border-blue-500" : "border-transparent"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* ── Price & Title ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.06 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-5"
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              {data.sell_price && (
                <p className="text-2xl font-bold text-emerald-600">
                  <FormatPrice number={data.sell_price} suffix=" บาท" />
                </p>
              )}
              {data.rent_price && (
                <p className={`${data.sell_price ? "text-base text-gray-500 mt-0.5" : "text-2xl font-bold text-blue-600"}`}>
                  <FormatPrice number={data.rent_price} suffix=" บาท/เดือน" />
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 flex-shrink-0">
              <Eye className="w-4 h-4" />
              <span className="text-xs">{data.views ?? 0} ครั้ง</span>
            </div>
          </div>

          <h1 className="text-base font-bold text-gray-900 leading-snug mb-2">{data.title}</h1>

          {data.location?.string && (
            <div className="flex items-center gap-1.5 text-gray-500 text-sm">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span>{[data.location.string.subdistrict, data.location.string.district, data.location.string.province].filter(Boolean).join(", ")}</span>
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-2 text-gray-400 text-xs">
            <Calendar className="w-3.5 h-3.5" />
            <FormatDateTime isoDateString={data.date} />
          </div>
        </motion.div>

        {/* ── Property Details ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4"
        >
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">รายละเอียด</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.rooms?.Bedroom > 0 && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Home className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">ห้องนอน</p>
                  <p className="text-sm font-bold text-gray-800">{data.rooms.Bedroom}</p>
                </div>
              </div>
            )}
            {data.rooms?.Bathroom > 0 && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-9 h-9 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Bath className="w-4 h-4 text-cyan-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">ห้องน้ำ</p>
                  <p className="text-sm font-bold text-gray-800">{data.rooms.Bathroom}</p>
                </div>
              </div>
            )}
            {data.floor > 0 && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-9 h-9 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Layers className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">ชั้น</p>
                  <p className="text-sm font-bold text-gray-800">{data.floor}</p>
                </div>
              </div>
            )}
            {data.land?.living_size && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Maximize2 className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">พื้นที่ใช้สอย</p>
                  <p className="text-sm font-bold text-gray-800">{data.land.living_size} {data.land.living_unit}</p>
                </div>
              </div>
            )}
            {(data.land?.rai > 0 || data.land?.ngan > 0 || data.land?.wah > 0) && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Trees className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">ขนาดที่ดิน</p>
                  <p className="text-sm font-bold text-gray-800">
                    {[data.land.rai && `${data.land.rai} ไร่`, data.land.ngan && `${data.land.ngan} งาน`, data.land.wah && `${data.land.wah} ตร.ว`].filter(Boolean).join(" ")}
                  </p>
                </div>
              </div>
            )}
            {data.furniture !== undefined && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-rose-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">เฟอร์นิเจอร์</p>
                  <p className="text-sm font-bold text-gray-800">{data.furniture ? "มีเฟอร์นิเจอร์" : "ไม่มี"}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Description ── */}
        {data.description && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4"
          >
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">รายละเอียดทรัพย์</p>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{data.description}</p>
          </motion.div>
        )}

        {/* ── Media ── */}
        {(data.media?.vr_url || data.media?.YoutubeURL) && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.11 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 space-y-3"
          >
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">สื่อ</p>
            {data.media.YoutubeURL && (
              <a
                href={data.media.YoutubeURL}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-100 hover:bg-red-100 transition-colors"
              >
                <div className="w-9 h-9 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Video className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-red-600">ดูวิดีโอ YouTube</span>
                <ExternalLink className="w-4 h-4 text-red-400 ml-auto" />
              </a>
            )}
            {data.media.vr_url && (
              <a
                href={data.media.vr_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-100 hover:bg-purple-100 transition-colors"
              >
                <div className="w-9 h-9 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Maximize2 className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-purple-600">ชมแบบ 360° VR</span>
                <ExternalLink className="w-4 h-4 text-purple-400 ml-auto" />
              </a>
            )}
          </motion.div>
        )}

        {/* ── Nearby ── */}
        {nearbyCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 space-y-2"
          >
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">สถานที่ใกล้เคียง</p>
            {nearbyCategories.map(([key, places]) => (
              <div key={key} className="border border-gray-100 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  onClick={() => setNearbyOpen(nearbyOpen === key ? null : key)}
                >
                  <span className="text-sm font-semibold text-gray-700">{categoryLabel(key)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{places.length} แห่ง</span>
                    <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${nearbyOpen === key ? "rotate-90" : ""}`} />
                  </div>
                </button>
                <AnimatePresence>
                  {nearbyOpen === key && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 py-2 space-y-2">
                        {places.map((p, i) => (
                          <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                            <span className="text-sm text-gray-700">{p.name}</span>
                            {p.distance && <span className="text-xs text-gray-400">{p.distance}</span>}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </motion.div>
        )}

        {/* ── Owner ── */}
        {ownerData && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.13 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4"
          >
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">เจ้าของทรัพย์</p>
            <div className="flex items-center gap-3 mb-4">
              <img
                src={ownerData.profile_image}
                alt={ownerData.uname}
                className="w-12 h-12 rounded-2xl object-cover ring-2 ring-gray-100"
                onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
              />
              <div>
                <p className="text-sm font-bold text-gray-900">{ownerData.uname}</p>
                {ownerData.verify_level > 0 && (
                  <span className="flex items-center gap-1 text-xs text-blue-600 font-semibold mt-0.5">
                    <CheckCircle className="w-3 h-3" /> ยืนยันแล้ว
                  </span>
                )}
              </div>
            </div>

            {/* Contact button */}
            {!showContact ? (
              <button
                onClick={handleGetContact}
                disabled={isLoadingContact}
                className="w-full h-12 bg-blue-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors shadow-sm disabled:opacity-60"
              >
                {isLoadingContact ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Phone className="w-4 h-4" />
                    ขอดูเบอร์ติดต่อ
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-2">
                {contactData?.phone && (
                  <a
                    href={`tel:${contactData.phone}`}
                    className="w-full h-12 bg-emerald-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors shadow-sm"
                  >
                    <Phone className="w-4 h-4" />
                    {contactData.phone}
                  </a>
                )}
                {contactData?.line_id && (
                  <div className="w-full h-12 bg-green-50 border border-green-200 text-green-700 rounded-xl font-semibold text-sm flex items-center justify-center gap-2">
                    Line: {contactData.line_id}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* ── Map ── */}
        {data.location?.coordinates?.lat && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.14 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
          >
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">ตำแหน่งที่ตั้ง</p>
              <a
                href={`https://www.google.com/maps?q=${data.location.coordinates.lat},${data.location.coordinates.lng}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-xs text-blue-500 font-semibold hover:underline"
              >
                <Navigation className="w-3.5 h-3.5" />
                นำทาง
              </a>
            </div>
            <iframe
              title="map"
              loading="lazy"
              src={`https://maps.google.com/maps?q=${data.location.coordinates.lat},${data.location.coordinates.lng}&z=15&output=embed`}
              className="w-full h-56 border-0"
            />
          </motion.div>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}
